import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import {
  CellValueChangedEvent,
  ColDef,
  DataTypeDefinition,
  DomLayoutType,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  IsRowSelectable,
  PaginationChangedEvent,
  RowClassParams,
  RowClickedEvent,
  RowDragEndEvent,
  RowNode,
  SelectionChangedEvent,
  SortChangedEvent,
  ValueFormatterLiteParams,
} from 'ag-grid-community';

import { CustomMultiSelectFilterComponent } from './components/custom-filter/custom-filter.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { expand, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Observable, Subject } from 'rxjs';
import { HttpService } from 'src/app/service/http/http.service';
import { RequestAttribute, RequestParams } from 'src/utils/models/http.interface';
import { CustomHeaderComponent } from './components/custom-header/custom-header.component';
import { AG_GRID_LOCALE_PT_BR } from './locale.ptBR';
import { CustomActionsComponent } from './components/custom-actions/custom-actions.component';
import { DetailRowComponent } from './components/detail-row/detail-row.component';
import { CustomTooltipComponent } from './components/custom-tooltip/custom-tooltip.component';
import { ModalLoadingComponent } from '../../loads/modal-loading/modal-loading.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ComponentType } from '@angular/cdk/portal';


export interface Actions {
  headerName?: string
  pinned?: 'left' | 'right' | undefined
  icons: Array<{ name: string, action: string, tooltip: string, color?: string }>
  width?: number | null
}


export interface CustomSubComponent {
  component: ComponentType<any>,
  params?: {
    dataKey?: string
    // e qualquer parametro que quiser
  }
}

export interface DetailRow {
  detailKey: string;
  contentKey: string;
  typeKey: string;
  backgroundColor: {};
  color: {};
  position?: 'left' | 'center' | 'right';
  fontWeight?: number;
  paddingLeft?: string;
  isGroupRow?: boolean
}

export interface TotalRowConfig {
  position: 'bottom' | 'top';
  fieldsToShow: Array<string>;
  label: string;
  hide?: boolean
}

export interface DisplayColumnsControl {
  mainTableColumns: ColumnsToHide[];
  subTableColumns: ColumnsToHide[];
}

export type ColumnsToHide = {
  show: boolean;
  column: string;
}


export type CustomCellDataTypeDefinition = {[cellDataType: string]: DataTypeDefinition;}

@Component({
  selector: 'dynamic-ag-table',
  templateUrl: './ag-grid.component.html',
  styleUrls: ['./ag-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('grid') grid!: AgGridAngular;
  @ViewChild('grid', { read: ElementRef }) agGridRef!: ElementRef;

  @Input() externalData: Array<any> = [];
  @Input() subTableInput: string = 'subTable';
  @Input() height = '500px';
  @Input() width = '100%';
  @Input() paginationEnabled = false;
  @Input() paginationWithoutActions = false;
  @Input() pageSize: number = 500;
  @Input() rowDragEnable = false;
  @Input() columnDefinition: ColDef[] = [];
  @Input() columnDefinitionSub: ColDef[] = [];
  @Input() expandedRowsEnable = false;
  @Input() expandAllOnLoad = false;
  @Input() isSubtable = false;
  @Input() headerColor: string = '#EBEBEB';
  @Input() headerColorSubtable: string = '#EBEBEB';
  @Input() tableTitle: string | undefined = undefined;
  @Input() rowHeight: number | undefined = undefined;
  @Input() headerHeight: number | undefined = undefined;
  @Input() headerPosition: 'left' | 'center' | 'right' | undefined = undefined;
  @Input() cellPosition: 'left' | 'center' | 'right' | undefined = undefined;
  @Input() autoSizeColumns: Array<string> = [];
  @Input() autoSizeColumnsSubTable: Array<string> = [];
  @Input() autoLayoutColumns = false
  @Input() endpoint = '';
  @Input() actions: Actions | undefined = undefined;
  @Input() checkBoxEnable = false;
  @Input() rowSelectionByClickEnable = false;
  @Input() detailRow: DetailRow | undefined = undefined;
  @Input() hideHeader = false;
  @Input() domLayout: DomLayoutType = 'normal'
  @Input() maxRows: number = 10
  @Input() compactHeaderMenu = true
  @Input() initialColSort!: { field: string, direction: 'asc' | 'desc' | null }
  @Input() initialColSortSub!: { field: string, direction: 'asc' | 'desc' | null }
  @Input() displayColumnsControl: DisplayColumnsControl = {mainTableColumns: [], subTableColumns: []}
  @Input() params: RequestParams = {
    limit: 1000,
    order: '',
    page: 1,
    sort: '',
  };
  @Input() requestMethod: 'POST' | 'GET' = 'POST';
  @Input() attributes: RequestAttribute[] = [];
  @Input() payload: {} | [] = {};
  @Input() totalRowConfig: TotalRowConfig | undefined = undefined
  @Input() totalRowConfigSubTable: TotalRowConfig | undefined = undefined
  @Input() defaultThemes:
    | 'ag-theme-alpine'
    | 'ag-theme-material'
    | 'ag-theme-balham' = 'ag-theme-alpine';
  @Input() gridOptions: GridOptions = {
    rowData: [],
    columnDefs: [],
    defaultColDef: {
      resizable: true,
      sortable: true,
      tooltipComponent: CustomTooltipComponent
    },
    pagination: true,
    animateRows: true,
    suppressDragLeaveHidesColumns: true,
    rowSelection: 'multiple',
    rowDragManaged: true,
    rowDragMultiRow: true,
    suppressRowClickSelection: false,
    rowMultiSelectWithClick: true,
    suppressCellFocus: true,
    singleClickEdit: true,
    enableCellTextSelection: true, 
    enableRangeSelection: true,   
    clipboardDelimiter: '\t',
    onCellMouseOver: (event) => {
    if(this.checkOnHover) {
    event.context.onHover = event.data.ignore_reorder ? null : event.node.id
    event.api.refreshCells({ force: true });
    }
    },
    onCellMouseOut: (event) => {
    if(this.checkOnHover) {
    event.context.onHover = null
    event.api.refreshCells({ force: true });
    }
    },
    // getRowId: (params) => params.data.id || params.data._id 
  };

  @Input() customDataTypeDefinitions: CustomCellDataTypeDefinition | undefined = undefined;
  @Input() customDataTypeDefinitionsSubtable: CustomCellDataTypeDefinition | undefined = undefined;
  @Input() radioRenderSubTableConfig: Array<string> | [] = [];
  @Input() showDetailJsons = false;
  @Input() getAllData = false;

  @Output() onAction = new EventEmitter<{ action: string; row: any }>();
  @Output() cellEditedEmitter = new EventEmitter<CellValueChangedEvent>();
  @Output() onSelectionRows = new EventEmitter<Array<{}>>();
  @Output() onRowClick = new EventEmitter<Array<{}>>();

  public expandedRows = new Set<string>();


  // armazendo os estados de ordenação
  sortStates: Record<string, 'asc' | 'desc' | null> = {};

  // regra padrão definida pelo desgin para alinhamento do conteudo das celulas
  mapCellAlign = {
    number: 'right',
    string: 'left',
  };

  noDataTemplate = `<img style="margin: auto; display: block" height="200" width="200" 
  src="../../../../assets/images/table.svg" alt="tabela sem dados" />`

  public popupParent: HTMLElement | null = document.body;


  private originalData: any[] = [];

  selectedRows: any[] = []; // Armazenar as linhas selecionadas
  selectedNode: IRowNode | null = null; // Armazenar a linha onde vamos colar as linhas recortadas

  tableData: Array<any> = [];
  count = 0;

  unsubscribeAll$ = new Subject<void>();
  private cleanupEvents: (() => void) | undefined;
  rowHeights: any;

  shouldUseExternalFilter = false;

  @Input() customSubComponent: CustomSubComponent | undefined = undefined

  defaultDataTypeDefinition: {[cellDataType: string]: DataTypeDefinition;} = {
    date: {
      baseDataType: 'date',
      extendsDataType: 'date',
      valueFormatter: (params: ValueFormatterLiteParams<any, Date>) =>
        params.value instanceof Date ? params.value.toLocaleDateString('pt-BR') : 'Invalid date',
    },
    number: {
      baseDataType: 'number',
      extendsDataType: 'number',
      valueFormatter: (params: ValueFormatterLiteParams<any, number>) => {
         return typeof params.value === 'number' ? params.value.toLocaleString('pt-BR') : ''
      }
       ,
    },
    text: {
      baseDataType: 'text',
      extendsDataType: 'text',
      valueFormatter: (params: ValueFormatterLiteParams<any, string>) =>
        params.value ?? '',
    },
  }
  checkOnHover = true;
  heightTable = 'fit-content';
 
  refreshTable(invalidRow: any[]) {
    invalidRow.forEach((data) => {
      if (this.gridOptions.rowData) {
        this.gridOptions.rowData[data.index]['error'] = true;
        if (!this.gridOptions.rowData[data.index].messageError) {
          this.gridOptions.rowData[data.index].messageError = {};
        }
        this.gridOptions.rowData[data.index]['messageError'][
          data.messageProp.prop
        ] = data.messageProp.message;
      }
    });
    this.grid.api!.setRowData(this.gridOptions.rowData || []);
    this.grid.api!.redrawRows();
  }

  clearErroTable() {
    if (this.gridOptions.rowData) {
      this.gridOptions.rowData
        .filter((data) => data?.error)
        ?.forEach((data) => {
          data['error'] = false;
          data['messageError'] = {};
        });
      this.grid.api!.setRowData(this.gridOptions.rowData);
      this.grid.api!.redrawRows();
    }
  }

  // funcao callback para estilizar as linhas
  getRowStyle = (params): any => {
    if (params.data && params.data.highlight) {
      return { backgroundColor: '#FFF3CD' };
    }

    const rowIndex = params.node.rowIndex || 0;
    if (params.data && params.data.error) {
      if (params.data?.isExpanded) {
        return { backgroundColor: '#e0e9f6', border: '1px solid red' };
      } else if (rowIndex % 2 !== 0) {
        return { backgroundColor: '#fbfcff', border: '1px solid red' };
      }
      return { backgroundColor: 'white', border: '1px solid red' };
    }

    if (params.data?.isSubtotal) {
      if (this.isSubtable && this.totalRowConfig?.position === 'bottom') {
        return {
          fontWeight: '600',
          backgroundColor: '#F0F0F0',
          color: '#48555F',
        };
      }
      return {
        fontWeight: 'bold',
        backgroundColor: '#e3eef6',
      };
    } else if (this.isSubtable) {
      return {
        backgroundColor: '#fff',
      };
    }

    return undefined;
  };

  private changeFieldControl$ = new Subject<ColDef>();

  constructor(
    private http: HttpService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private el: ElementRef
  ) {}

  // configurações genericas para colunas
  handleColfDefinitions() {
    if (this.rowDragEnable) {
      this.columnDefinition[0].rowDrag = (params) =>
        !params.data?.isSubtotal && !params.data?.isExpanded;
    }
    if (this.actions) {
      this.setActionsCol();
    }

    this.columnDefinition[0]['checkboxSelection'] = this.checkBoxEnable;
    this.columnDefinition[0]['headerCheckboxSelection'] = this.checkBoxEnable;

    for (let i = 0; i < this.columnDefinition.length; i++) {
      // this.columnDefinition[i].tooltipField = this.columnDefinition[i].field

      if (this.columnDefinition[i].editable) {
        this.columnDefinition[i]['cellStyle'] = (params) => {
          if (params.data?.isSubtotal) return;
          return { padding: '8px' };
        };
        

        if (this.columnDefinition[i]['cellRenderer'] === undefined) {
          const dataType = this.columnDefinition[i].cellDataType;
          const formatter = this.defaultDataTypeDefinition?.[dataType as string]?.valueFormatter;
          this.columnDefinition[i]['cellRenderer'] = (params) => {
            if (params.data?.isSubtotal) return params.value;
            const value = formatter ? formatter(params) : params.value;

            return `
              <div style="
                height: 100%;
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding: 4px;
                box-sizing: border-box;
                border: 1px solid #ccc;
                border-radius: 4px;">${value}</div>
            `;

            // <div style="display: flex; flex-direction: column; height: 100%; justify-content: center;">
            //     <div style="
            //       height: 21px;
            //       width: 100%;
            //       display: flex;
            //       align-items: center;
            //       justify-content: flex-end;
            //       padding: 4px;
            //       box-sizing: border-box;
            //       border: 1px solid #ccc;
            //       border-radius: 4px;">${value}</div>
            //   </div>
          };
        }

        this.columnDefinition[i]['valueSetter'] = (params) => {
          if (params.newValue === null || params.newValue === '') {
            return false;
          }
          if (params.colDef.field)
            params.data[params.colDef.field] = params.newValue;
          return true;
        };
      }

      /* 
      ********** LÓGICA DE POSICIONAMENTO DAS CÉLULAS **********
      - Se for definido o input `cellPosition`, ele será usado como alinhamento para todas as células.
      - Caso `cellPosition` não seja definido, o alinhamento será baseado no tipo do valor da célula:
          • Números → alinhamento à direita
          • Strings → alinhamento à esquerda
          • Outros → alinhamento centralizado
      - Se a coluna já tiver um `cellStyle` com `textAlign`, esse valor será mantido como prioridade.
      */
      const originalCellStyle = this.columnDefinition[i].cellStyle;

      this.columnDefinition[i].cellStyle = (params) => {
        const base =
          typeof originalCellStyle === 'function'
            ? originalCellStyle(params)
            : originalCellStyle || {};

        const value = params.value;

        let align: string | number = 'center';

        if (base && base['textAlign']) {
          align = base['textAlign'];
        } else if (this.cellPosition) {
          align = this.cellPosition;
        } else if (typeof value === 'number') {
          align = 'right';
        } else if (typeof value === 'string') {
          align = 'left';
        }

        const needsRightBorder =
          params.colDef.field !== 'expand' &&
          params.colDef.field !== 'expand-fake';

        return {
          ...base,
          ...(needsRightBorder ? { borderRight: '1px solid #D4D9E6' } : {}),
          textAlign: align,
        };
      };

      // posição custom do header via INPUT
      if (!!this.headerPosition) {
        this.columnDefinition[i][
          'headerClass'
        ] = `custom-header ${this.headerPosition}`;
      }

      // adiciona os botoes de ação
      if (
        this.columnDefinition[i].colId === 'action' &&
        this.actions
      ) {
        // adiciona o component customizado de actions dentro da cell
        this.columnDefinition[i]['cellRenderer'] = CustomActionsComponent;

        // adciona a callBack onAction via params no component customizado e faz o bind de onActionClick para referenciar a tabela
        this.columnDefinition[i]['cellRendererParams'] = {
          ...this.columnDefinition[i]['cellRendererParams'],
          onAction: this.onActionClick.bind(this),
        };
      }


    }
  }

  //recebe o evento disparado pela coluna de ação
  onActionClick(action: string, rowData: any): void {
    console.log(`Ação: ${action} - Linha:`, rowData);
    this.onAction.emit({ action, row: rowData });
  }

  // cosntroi a coluna de ação
  setActionsCol() {
    if (!this.actions) {
      console.error('Input "actions" obrigtório para renderizar as ações!');
    }
    const actionsColumnExists = this.columnDefinition.some(
      (col) => col.field === 'actions'
    );

    if (actionsColumnExists) return;

    this.columnDefinition.push({
      headerName: this.actions?.headerName || 'Ações',
      width: this.actions?.width,
      field: 'actions',
      filter: false,
      headerComponentParams: false,
      pinned: this.actions?.pinned || 'right',
      colId: 'action',
      cellClass: 'action-cells',
      headerClass: 'action-header',
      cellRendererParams: {
        icons: this.actions?.icons,
      },
    });
  }

  ngOnInit(): void {
    this.defaultDataTypeDefinition = {...this.defaultDataTypeDefinition, ...this.customDataTypeDefinitions}
    // coloca cor no header dinamicamente via input
    this.el.nativeElement.style.setProperty(
      '--ag-custom-header-color',
      this.isSubtable ? this.headerColorSubtable : this.headerColor
    );
    this.handleColfDefinitions();

    this.setGridOptionsConfigurations();
    if (this.externalData.length > 0) {
      if(this.detailRow?.isGroupRow){
        this.externalData = this.externalData.map((item) => ({...item, isVisible: true, expanded: true}))

      }
      this.tableData = this.externalData;
      this.originalData = this.externalData;
      this.gridOptions['rowData'] = this.externalData;
    } else {
      this.gridOptions['rowData'] = [...this.tableData];
      this.loadData();
    }

    this.totalRowsVisible = this.externalData.length;
    this.controlHeightDynamically();
  }

  ngAfterViewInit(): void {
    this.grid.api.clearFocusedCell();
    this.restoreWheelEvent();

    // hide header by input
    if (!this.isSubtable && this.hideHeader) {
      const header = this.el.nativeElement.getElementsByClassName(
        'ag-header'
      ) as HTMLElement;
      header[0].style.display = 'none';
    }
  }

  // restaura o evento de wheel do mouse para poder fazer o scroll
  restoreWheelEvent() {
    if (this.rowDragEnable) {
      const nativeElement = this.agGridRef?.nativeElement as HTMLElement;
      const viewport = nativeElement?.querySelector(
        '.ag-body-viewport'
      ) as HTMLElement;
      const horizontalViewPort = this.agGridRef?.nativeElement.querySelector(
        '.ag-body-horizontal-scroll-viewport'
      ) as HTMLElement;

      if (!viewport) return;

      let dragging = false;

      const onPointerDown = (e: PointerEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('.ag-row-drag')) {
          dragging = true;
        }
      };

      const onPointerUp = () => {
        dragging = false;
      };

      const onWheel = (e: WheelEvent) => {
        if (dragging && viewport) {
          viewport.scrollTop += e.deltaY;
          e.preventDefault();
        }
      };

      document.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('wheel', onWheel, { passive: false });

      this.cleanupEvents = () => {
        document.removeEventListener('pointerdown', onPointerDown);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('wheel', onWheel);
      };
    }
  }

  rowHeightsMap: Record<string, number> = {};

  // configurações genericas do GridOptions
  setGridOptionsConfigurations() {
    // linha detalhada generica ou subtabela
    this.gridOptions = {
      ...this.gridOptions,
      isExternalFilterPresent: () => {
        return this.shouldUseExternalFilter;
      },
      doesExternalFilterPass: (node) => {
        return node.data?.isVisible !== false;
      },
      isFullWidthRow: (params) => {
        if (params.rowNode.data?.isDetailRow) {
          return true;
        }
        if (!!this.detailRow && params.rowNode.data[this.detailRow.detailKey]) {
          return true;
        }
        return false;
      },
      fullWidthCellRenderer: 'agDetailPanelRenderer',
      components: {
        agDetailPanelRenderer: DetailRowComponent,
      },
    };

    // parametros para linha detalhada
    this.gridOptions.fullWidthCellRendererParams = {
      onload: (panelElement: HTMLElement, rowNode, subViewChild) => {
        setTimeout(() => {
        const height = panelElement.offsetHeight;
        this.rowHeightsMap[rowNode.id || rowNode._id] = height;
          this.grid.api?.resetRowHeights();
        }, 0);
      },
      subTableEdited: (rowNode, parentId) => {
        this.reflectCellEdit(parentId, rowNode)
        this.cellEditedEmitter.emit(rowNode);
      },
      changeFieldControl: this.changeFieldControl$,
      genericDetailRowConfig: this.detailRow,
      columnsChanged: () => this.displayColumnsControl?.subTableColumns
    };

    // força a celula perder o focus ao clicar ou navegar na tabela
    this.gridOptions['stopEditingWhenCellsLoseFocus'] = true;

    // quantidade de linhas exbidas por página
    this.gridOptions['paginationPageSize'] = this.pageSize;

    // funcao callback para adicionar classes na tabela
    this.gridOptions['getRowClass'] = (params: RowClassParams) => {
      const classes: string[] = [];
      if(this.expandedRowsEnable) classes.push('has-expand-row')
      if (params.data.error) classes.push('error-line');
      if ((params.data.isExpanded) && !params.data[!!this.detailRow ? this.detailRow.detailKey : '']) classes.push('expanded-row');
      if((!!this.actions || this.columnDefinition.some(col => col.field === 'actions'))) classes.push('row-actions');
      if(this.grid && this.grid.api && this.grid.api.isAnyFilterPresent()) classes.push('row-filter');
      return classes.length > 0 ? classes : undefined;
    };

    // ativa ou desativa seleção por click na linha
    this.gridOptions['suppressRowClickSelection'] =
      !this.rowSelectionByClickEnable;

    // função callback para definir altura da linha
    this.gridOptions.getRowHeight = (params) => {
      const rowId = params.node.id || 0;
      if (params.data?.isDetailRow && this.rowHeightsMap[rowId]) {
        return this.rowHeightsMap[rowId];
      }
      return undefined;
    };

    //evento disparado ao filtrar
    this.gridOptions.onFilterChanged = (event: FilterChangedEvent) => {
      if(!!this.totalRowConfig){
        this.applySubtotalRow(this.totalRowConfig)
      }
      
      this.cleanAllDetailRows();
    };

    //evento disparado ao fazer sort
    this.gridOptions.onSortChanged = (event: SortChangedEvent) => {
      this.cleanAllDetailRows();
    };

    // constroi a coluna de expand para inserir o icone de expansao
    if (
      this.expandedRowsEnable &&
      !this.columnDefinition.find((col) => col.field === 'expand')
    ) {
      this.columnDefinition.unshift({
        field: 'expand',
        width: this.checkBoxEnable ? 80 : 60,
        maxWidth: this.checkBoxEnable ? 80 : 60,
        minWidth: this.checkBoxEnable ? 80 : 60,
        suppressMovable: true,
        lockPosition: true,
        lockPinned: true,
        suppressMenu: true,
        pinned: 'left',
        suppressSizeToFit: true,
        suppressColumnsToolPanel: true,
        cellClass: 'expand-row-cell',
        headerClass: 'expand-col',
        rowDrag: this.rowDragEnable,
        cellStyle: (params) => {
          return params.data.isExpanded
            ? { borderLeft: '1px solid #0074C0' }
            : undefined;
        },
        cellRenderer: (params) => {
          if (params.data.isSubtotal) return undefined;
          const hasSubTable =
            (this.expandedRowsEnable &&
              Array.isArray(params.data[this.subTableInput]) &&
              params.data[this.subTableInput].length > 0) ||
            (this.expandedRowsEnable &&
              this.radioRenderSubTableConfig.length > 0) ||
            !!this.customSubComponent;

          const icon = !hasSubTable 
            ? '' 
            : params.data?.isExpanded 
              ? 'expand_less' 
              : 'expand_more';
          return `
            <div class="expand-container" style="
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
            ">
              <button class="expand-button" style="
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <span class="material-icons" style="font-size: 20px; transition: 0.2s;">
                  ${icon}
                </span>
              </button>
            </div>
          `;
        },
      });
    }
    // definição das colunas
    this.gridOptions['columnDefs'] = this.columnDefinition;

    // define quais linhas sao selecionaveis/marcaveis
    this.gridOptions['isRowSelectable'] = this.isRowSelectable;

    //tradução da tabela para pt-br
    this.gridOptions.localeText = AG_GRID_LOCALE_PT_BR;

    //adiciona components customizados aos headers
    if (this.gridOptions['defaultColDef']) {
      this.gridOptions.tooltipShowDelay = 0;
      // this.gridOptions.defaultColDef.tooltipComponent = CustomTooltipComponent,

      //custom header que é o container das outras ações
      this.gridOptions['defaultColDef']['headerComponent'] =
        CustomHeaderComponent;
      this.gridOptions['defaultColDef']['headerComponentParams'] = {
        singleMenu: this.compactHeaderMenu,
        onSort: (sort: 'asc' | 'desc', column) => {
          this.handleSort(sort, column);
        },
        getSortDirection: (colId: string) => this.sortStates[colId] ?? null,
      };

      // custom filter
      this.gridOptions['defaultColDef']['filter'] =
        CustomMultiSelectFilterComponent;
      // parametros do custom filter
      this.gridOptions['defaultColDef']['filterParams'] = {
        singleMenu: this.compactHeaderMenu,
         onSort: (sort: 'asc' | 'desc', column) => {
          this.handleSort(sort, column);
        },
        getSortDirection: (colId: string) => this.sortStates[colId] ?? null,
        sortState: () => this.sortStates

      }
    }
  }

  // funcao para atualizar os valores da coluna da subtable reativamente 
  updateFieldColumnSubTable(col: ColDef) {
    this.changeFieldControl$.next(col);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // quando inputs mudam o estado precisa  reconfigurar as colunas e redrawRows
    if (changes && this.grid) {
      if (changes['rowDragEnable']) {
        this.columnDefinition = this.columnDefinition.map((col, index) => {
          if (index === 0) {
            return { ...col, rowDrag: this.rowDragEnable };
          }
          return col;
        });

        this.grid.api.setColumnDefs(this.columnDefinition);
      }
      setTimeout(() => {
        this.grid.api.redrawRows();
      });
    }
    // oculta ou exibe as colunas quando o valor de displayColumnsControl muda
    if(changes && this.grid && changes['displayColumnsControl']){
      changes['displayColumnsControl'].currentValue
      this.columnsVisibilityControl(changes['displayColumnsControl'].currentValue)
      this.displayColumnsControl = changes['displayColumnsControl'].currentValue
    }
  }

  // retorna as linhas que podem ser selecionadas usa o params para pegar as linhas
  public isRowSelectable: IsRowSelectable = (params) => {
    return !params.data.isSubtotal;
  };

  // define o sort inicial de uma coluna baseado no input initialColSort
  initialSortColumn() {
    this.columnDefinition.forEach((col) => {
      if(col.field === this.initialColSort.field) {
        this.handleSort(this.initialColSort.direction, this.initialColSort.field)
      }
    })
  }

  // primeira vez que atabela renderiza data
  public onDataRendered(params: FirstDataRenderedEvent) {
    if(this.initialColSort) {
      this.initialSortColumn()
    }
    // cria e calcula a linha de total/subtotal
    if (!!this.totalRowConfig) {
      this.applySubtotalRow(this.totalRowConfig);
    }
    // caso presente no input faz o tamanho da colnuna se ajustar ao conteudo
    if (this.autoSizeColumns.length || this.autoSizeColumnsSubTable.length) {
      const columnsSize = this.isSubtable
        ? this.autoSizeColumnsSubTable
        : this.autoSizeColumns;
      columnsSize.forEach((i) => {
        this.grid.columnApi.autoSizeColumn(i);
      });
    }

    if(!this.actions?.width){
      this.grid.columnApi.autoSizeColumn('action')
    }
    this.totalRowsVisible = this.grid.api.getDisplayedRowCount();
    this.controlHeightDynamically()

  }

  controlHeightDynamically(){
    if(this.totalRowsVisible >= this.maxRows || this.totalRowsVisible === 0){
      this.domLayout = 'normal'
      this.heightTable = this.height
    }else{
      this.domLayout = 'autoHeight'
      this.heightTable = 'fit-content'
    }
  }

  // assim que o component e criado
  onGridReady(params: any) {
    if(this.autoLayoutColumns){
      const headerViewPort = document.querySelector(`.ag-header-viewport`);
      if (headerViewPort) {
        const columnApi = params.columnApi;
        const allColumns = columnApi.getAllGridColumns();
        let headersSumWidth = 0;
  
        allColumns.forEach((col) => {
          const colId = col.getColId();
          const headerElement = document.querySelector(
            `.ag-header-cell[col-id="${colId}"]`
          );
          if (headerElement) {
            headersSumWidth += headerElement.clientWidth;
          }
        });
  
        if (headersSumWidth < headerViewPort.clientWidth) {  
          const updatedColDefs = this.grid.columnApi.getColumns()?.map((col) => {
            const colDef = col.getColDef();
            return {
              ...colDef,
              flex: 1,
              minWidth: colDef.minWidth || 100,
            };
          });
  
          if (updatedColDefs) {
            this.grid.api.setColumnDefs(updatedColDefs);
          }
        } else {
          this.grid.columnApi.autoSizeAllColumns();
        }
      }
    }
    this.grid = params;

  }

  applySubtotalRow(totalRowConfig): void {
    if(totalRowConfig.hide){
      this.grid.api.setPinnedTopRowData();
      this.grid.api.setPinnedBottomRowData();
      return;
    }
    const allFilterModels = this.grid.api.getFilterModel();
    const isAnyFilterPresent = Object.values(allFilterModels).some((model: any) => {
      return Array.isArray(model.selectedValues) && model.selectedValues.length > 0;
    });
    let data: any = []
    if(isAnyFilterPresent){
      this.grid.api.forEachNodeAfterFilterAndSort(node => {
        data.push(node.data)
      })
    }else{
      this.grid.api.forEachNode((row: RowNode) => {
        data.push(row.data)
      })
      // data = this.gridOptions['rowData']
    }

    if (
      !data ||
      data.length === 0
    ) return;

    // Criar a linha de subtotal
    const subtotalRow = this.generateSubtotalRow(data);
    if (totalRowConfig.position === 'top') {
      // Define a linha como fixa no topo
      this.grid.api.setPinnedTopRowData([subtotalRow]);
    } else {
      // Define a linha como fixa no rodapé
      this.grid.api.setPinnedBottomRowData([subtotalRow]);
    }
  }

  generateSubtotalRow(data: any[]): any {
    if (!data || data.length === 0) return {};
    const idexFirstColumn = this.expandedRowsEnable || this.isSubtable ? 1 : 0;
    const firstColumnField =
      this.columnDefinition[idexFirstColumn]?.field ?? 'defaultColumn';
    // Cria as definicoes da linha de subtotal
    const subtotalRow: any = {
      [firstColumnField]: this.totalRowConfig?.label,
      isSubtotal: true,
    };


    this.totalRowConfig?.fieldsToShow.forEach((field) => {
        subtotalRow[field] = data.reduce((sum, row) => {
          if (!row.isGroup) {
            return sum + (row[field] || 0);
          }
          return sum;
        }, 0);
    });

    return subtotalRow;
  }

  // quando a paginação muda
  public onPaginationChanged(params: PaginationChangedEvent) {
    return;
  }

  // request loop
  loadData(): void {
    if (this.endpoint) {
      const loading = this.modalLoading();
      if(!this.getAllData){ // FAZ UM LOOP DE REQUEST 
         this.getData()
        .pipe(
          expand(() =>
            this.tableData.length < this.count ? this.getData() : EMPTY
          ),
          finalize(() => {
            if (this.detailRow?.isGroupRow) {
              this.tableData = this.tableData.map(item => ({ ...item, isVisible: true, expanded: true }))
            }
            if (this.gridOptions.api) {
              this.gridOptions.api.setRowData([...this.tableData]);
              this.originalData = [...this.tableData];
            } else {
              this.gridOptions['rowData'] = [...this.tableData];
              this.originalData = [...this.tableData];
            }

            loading.close();
             this.controlHeightDynamically()

          })
        )
        .subscribe();
      }else{  // PEGA TODOS OS DADOS SEM LIMITE
        this.http.genericGetNoProject(this.endpoint).subscribe({
          next: (value: {count: number, rows: Array<{}>}) => {
            if (this.gridOptions.api) {
              this.gridOptions.api.setRowData([...value.rows]);
              this.tableData = value.rows;
              this.originalData = [...this.tableData];
            } else {
              this.gridOptions['rowData'] = [...value.rows];
              this.tableData = value.rows;
              this.originalData = [...this.tableData];
            }
            loading.close();
             this.controlHeightDynamically()



          }
        })
      }
     
    }
  }

  private modalLoading(): MatDialogRef<ModalLoadingComponent> {
    return this.dialog.open(ModalLoadingComponent, {
      autoFocus: true,
      disableClose: true,
      width: '400px',
      data: {
        disableClose: true,
        title: 'Carregando',
        text: 'Por favor espere alguns momentos',
      },
      panelClass: 'loading-modal',
    });
  }

  //request
  private getData(): Observable<void> {
    const request$ =
      this.requestMethod === 'GET'
        ? this.http.genericGetParams(
            this.endpoint,
            this.params,
            this.attributes
          )
        : this.http.genericPostParams(
            this.endpoint,
            this.params,
            this.attributes,
            this.payload
          );

    return request$.pipe(
      takeUntil(this.unsubscribeAll$),
      tap((res: any) => {
        (this.params ??= {}).page = (this.params.page ?? 0) + 1;
        this.count = res.count;
        this.tableData.push(...res.rows);
      }),
      map(() => void 0)
    );
  }

  // qualquer mudança na seleção
  selectionChanged(params: SelectionChangedEvent) {
  const selected = this.grid.api.getSelectedRows();

  const filteredNodes = [];
  this.grid.api.forEachNodeAfterFilter((node) => {
    if (node.isSelected()) {
      filteredNodes.push(node.data);
    }
  });

  this.onSelectionRows.emit(filteredNodes);
}

  // recorta as linhas selecionadas
  cutSelectedRows() {
    this.selectedRows = this.gridOptions.api!.getSelectedRows();

    const rowNodes = this.gridOptions.api!.getSelectedNodes();
    rowNodes.forEach((node: IRowNode) => {
      node.setSelected(false);
      this.gridOptions.api!.applyTransactionAsync({ remove: [node.data] });
    });
  }

  // cola as linhas recortadas acima ou abaixo da linha selecionada
  pasteRows(position: 'above' | 'below') {
    if (this.selectedRows.length === 0) {
      alert('Sem linhas recortadas para colar.');
      return;
    }

    this.selectedNode = this.grid.api!.getSelectedNodes()[0];

    if (!this.selectedNode || this.selectedNode.rowIndex === null) {
      alert('Selecione uma linha válida para colar.');
      return;
    }

    const selectedRowId: string = this.selectedNode.id
      ? this.selectedNode.id
      : '';

    const targetRowNode = this.grid.api!.getRowNode(selectedRowId);

    if (!targetRowNode) {
      alert('Linha de destino não encontrada.');
      return;
    }

    const targetRowIndex = targetRowNode.rowIndex;
    if (targetRowIndex === null || targetRowIndex === undefined) {
      alert('Índice de linha inválido.');
      return;
    }

    let insertIndex: number;

    const displayedRowCount = this.grid.api!.getDisplayedRowCount();
    if (displayedRowCount === 0) {
      alert('Nenhuma linha visível após filtro.');
      return;
    }

    if (position === 'below') {
      insertIndex = targetRowIndex + 1;
    } else {
      insertIndex = targetRowIndex;
    }

    const visibleRowData: any[] = [];
    for (let i = 0; i < displayedRowCount; i++) {
      const rowNode: any = this.grid.api!.getDisplayedRowAtIndex(i);
      visibleRowData.push(rowNode.data);
    }

    const newRowData = [
      ...visibleRowData.slice(0, insertIndex),
      ...this.selectedRows.map((row) => ({ ...row, highlight: true })),
      ...visibleRowData.slice(insertIndex),
    ];

    this.grid.api!.setRowData(newRowData);

    this.grid.api!.redrawRows();

    this.selectedRows = [];
    this.selectedNode = null;
  }

  // evento disparado pela edição de uma celula
  onCellValueChanged(event: CellValueChangedEvent) {
    const newValue = event.newValue
    const oldValue = event.oldValue
    if (!!this.totalRowConfig) {
      this.applySubtotalRow(this.totalRowConfig);
    }
    // this.refreshAllFilterModel();
    const colId = event.column.getColId();
    this.grid.api.getFilterInstance(colId, (filterInstance: any) => {
      if (filterInstance?.refreshOptions) {
        filterInstance.refreshOptions(oldValue, newValue);
      }
    });
    this.cellEditedEmitter.emit(event);
  }

  // especifico tabela cosigua
  @Input() collumnsToReflectEdit: string [] = ['status']
  reflectCellEdit(parentId: number | string, linhaEitada) {
    if(!this.isSubtable){
      this.grid.api.forEachNode((node) => {
      if (node.data.id === parentId) {  
        this.grid.api.refreshCells({
          rowNodes: [node], 
          columns: this.collumnsToReflectEdit, 
          force: true
        });
      }
    });

    const allFilterInstances = this.grid.api.getFilterModel();
      Object.keys(allFilterInstances).forEach((key) => {
        this.grid.api.getFilterInstance(key, (filterInstance: any) => {
        if (filterInstance) {
          filterInstance.extractUniqueValues(false);
        }
      });
    });
    }
  } 

  // evento de click na linha
  onRowClicked(event: RowClickedEvent) {
    this.onRowClick.emit(event.data)
    if (
      (this.expandedRowsEnable &&
        Array.isArray(event.data[this.subTableInput]) &&
        event.data[this.subTableInput].length > 0) ||
      (this.expandedRowsEnable && this.radioRenderSubTableConfig.length > 0) ||
      !!this.customSubComponent
    ) {
      this.expandRow(event);
    }
    if(event.data?.isGroup){
      this.expandOrColapseGroup(event)
    }
  }

    setRowData(data: any[]) {
    this.originalData = data; // mantém original sempre
    this.grid.api.setRowData(data);
    this.totalRowsVisible = this.grid.api.getDisplayedRowCount();
    this.controlHeightDynamically();
    this.cdr.markForCheck();
  }

  
 // expande ou esconde a subtabela abaixo da linha clicada
  private expandRow(event: RowClickedEvent): void {
    const clickedData = event.data;
    if (clickedData?.isDetailRow) return;

    const itemId = clickedData?.id || clickedData._id;
    
    if (!itemId) return;

    let newData: any[] = [];
    this.grid.api.forEachNode((node) => {
      newData.push(node.data);
    });

    const parentIndex = newData.findIndex((d) => (d.id || d._id) === itemId);
    if (parentIndex < 0) return;

    const parentItem = newData[parentIndex];
    parentItem.isExpanded = !parentItem.isExpanded;

    if (parentItem.isExpanded) {
      this.expandedRows.add(itemId);
    } else {
      this.expandedRows.delete(itemId);
    }

    // remove detalhes antigos
    newData = newData.filter((d) => !(d.isDetailRow && d.parentId === itemId));

    // insere de novo se expandido
    if (parentItem.isExpanded) {
      const detailRow = this.createDetailRow(parentItem);
      newData.splice(parentIndex + 1, 0, detailRow);
    }

    this.grid.api.setRowData(newData);
  }

  public reopenExpandedRows(): void {
    if(this.expandedRows.size > 0){
          let newData: any[] = [];
        this.grid.api.forEachNode((node) => {
          newData.push(node.data);
        });

        this.expandedRows.forEach((id) => {
          const parentIndex = newData.findIndex((d) => (d.id || d._id) === id);
          if (parentIndex >= 0) {
            newData[parentIndex].isExpanded = true;
            const detailRow = this.createDetailRow(newData[parentIndex]);
            newData.splice(parentIndex + 1, 0, detailRow);
          }
        });

      this.grid.api.setRowData(newData);
        }

}

  // cria a linha aonde a subtabela é renderizada
  createDetailRow(row: any) {
    const coldef = [...this.columnDefinitionSub];
    coldef.unshift({
      field: 'expand-fake',
      width: 20,
      maxWidth: 20,
      minWidth: 20,
      suppressSizeToFit: true,
    });
    if (this.actions) {
      this.setActionsCol();
    }

    return {
      showDetailJsons: this.showDetailJsons,
      radioRenderSubTableConfig: this.radioRenderSubTableConfig,
      isDetailRow: true,
      parentId: row.id || row._id,
      columnDefs: coldef,
      rowData: !!row[this.subTableInput] ?  [...row[this.subTableInput]] : [],
      dataRow: {...row},
      gridOptions: this.gridOptions,
      totalRowConfig: this.totalRowConfigSubTable,
      autoSizeColumnsSubTable: this.autoSizeColumnsSubTable,
      rowHeight: !!this.rowHeight ? this.rowHeight : 40,
      rowDragEnable: this.rowDragEnable,
      autoLayoutColumns: this.autoLayoutColumns,
      compactHeaderMenu: this.compactHeaderMenu,
      customDataTypeDefinitionsSubtable: this.customDataTypeDefinitionsSubtable
    };
  }

  // mantem as subtabelas abertas
  rebuildRowsWithDetail() {
    const updatedRows: any[] = [];

    this.tableData.forEach((row: any) => {
      updatedRows.push(row);

      if (row.isExpanded) {
        updatedRows.push(this.createDetailRow(row));
      }
    });

    this.gridOptions.api?.setRowData(updatedRows);
  }

  // faz o refresh da subtabela, nesse caso apenas esta inserindo erros externos
  refreshSubTable(invalidRow: any[]) {
    invalidRow.forEach((data) => {
      this.tableData[data.indexTable]['error'] = true;
      this.tableData[data.indexTable][this.subTableInput][data.indexSubTable][
        'error'
      ] = true;
      if (
        !this.tableData[data.indexTable][this.subTableInput][data.indexSubTable]
          .messageError
      ) {
        this.tableData[data.indexTable][this.subTableInput][
          data.indexSubTable
        ].messageError = {};
      }
      this.tableData[data.indexTable][this.subTableInput][data.indexSubTable][
        'messageError'
      ][data.messageProp.prop] = data.messageProp.message;
    });
    this.grid.api!.redrawRows();
  }

  // limpa os erros externos
  clearErroSubTable(canDeleteIsExpanded = false) {
    this.tableData
      .filter((data) => data?.error)
      ?.forEach((data) => {
        delete data['error'];
        if (canDeleteIsExpanded) {
          delete data['isExpanded'];
        }
        data[this.subTableInput]
          .filter((subData) => subData?.error)
          ?.forEach((subData) => {
            delete subData['error'];
            delete subData['messageError'];
          });
      });
    this.grid.api!.redrawRows();
  }

  // fecha e limpa todas as subtabelas abertas
  public cleanAllDetailRows(): void {
    const rowsToRemove: any[] = [];

    this.grid.api!.forEachNode((node) => {
      const data = node.data;
      if (!data) return;

      if (data.isDetailRow) {
        rowsToRemove.push(data);
      } else {
        data.isExpanded = false;
      }
    });

    if (rowsToRemove.length > 0) {
      this.grid.api!.applyTransaction({ remove: rowsToRemove });
    }

    this.grid.api.redrawRows();
  }

  // faz o sort da tabela
  handleSort(direction: 'asc' | 'desc' | null, column: string) {
    this.sortStates[column] = direction;
    if(this.detailRow?.isGroupRow){ // FAZ O SORTE PARA LINHAS AGRUPADAS
      const sortedData: any[] = [];
      let currentGroup: any = null;
      let currentGroupItems: any[] = [];
    
      this.tableData.forEach((row, index) => {
        if (row.isGroup) {
          if (currentGroup) {
            currentGroupItems.sort((a, b) => {
              const valueA = a[column];
              const valueB = b[column];
              if (valueA < valueB) return direction === 'asc' ? -1 : 1;
              if (valueA > valueB) return direction === 'asc' ? 1 : -1;
              return 0;
            });
            sortedData.push(...currentGroupItems);
          }
          currentGroup = row;
          sortedData.push(row);
          currentGroupItems = [];
        } else {
          currentGroupItems.push(row);
        }
      });
    
      if (currentGroup) {
        currentGroupItems.sort((a, b) => {
          const valueA = a[column];
          const valueB = b[column];
          if (valueA < valueB) return direction === 'asc' ? -1 : 1;
          if (valueA > valueB) return direction === 'asc' ? 1 : -1;
          return 0;
        });
        sortedData.push(...currentGroupItems);
      }
    
      this.tableData = [...sortedData];
    }else{ // FAZ O SORT EM TODAS AS LINHAS
      this.resetSortColumns(column)
      this.tableData.forEach((e) => (e['isExpanded'] = false));
      if (this.gridOptions.rowData)
        this.gridOptions.rowData.forEach((e) => (e['isExpanded'] = false));

      if (direction) {
        this.tableData.sort((a, b) => {
          const valueA = a[column];
          const valueB = b[column];

          if (valueA < valueB) return direction === 'asc' ? -1 : 1;
          if (valueA > valueB) return direction === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }
  
    setTimeout(() => {
      this.grid.api.setRowData(this.tableData);
      this.grid.api.redrawRows();
    });
  }

  // apos realizar o sort em outra coluna reseta o estado dos sorts das outras colunas
   resetSortColumns(column) {
    Object.keys(this.grid.api.getFilterModel()).forEach(model => {
      if(model !== column) {
        this.grid.api.getFilterInstance(model, (instance: any) => {
          instance.sortOptions.map((sort) => sort.active = false)
        })
      }
    })
  }

  // captura o evento ao finalizar o drag
  public onDragEnd(params: RowDragEndEvent) {
    this.grid.api.deselectAll();
  }

  // destroi a instancia do filtro para garantir que esteja sempre atualizado ao abrir
  refreshAllFilterModel() {
    const allFilterInstances = this.grid.api.getFilterModel();
    Object.keys(allFilterInstances).forEach((key) => {
      this.grid.api.destroyFilter(key);
    });

    this.grid.api.redrawRows()
  }

  // esconde ou exibe as linhas agrupadas
  expandOrColapseGroup(event: RowClickedEvent) {
    const groupRow = event.data;
    const groupId = groupRow.id;
    groupRow['expanded'] = !groupRow['expanded'] 
    this.grid.api.forEachNode((node) => {
      const isChild = node.data?.groupId === groupId && !node.data?.isGroup;
      if (isChild) {
        node.data.isVisible = !node.data.isVisible;
      }
  
      if (node.data?.isVisible === undefined) {
        node.data.isVisible = true;
      }
    });
  
    this.shouldUseExternalFilter = true;
    this.grid.api.onFilterChanged();
  }

  // esconde ou exibe as colunas baseado no valor do input displayColumnsControl
  columnsVisibilityControl(columnsToHide: DisplayColumnsControl){
    columnsToHide.mainTableColumns.forEach(i => {
      this.grid.columnApi.setColumnVisible(i.column, i.show)
    })

    
  }
  
  totalRowsVisible = 0;
  onFilterChanged(){
    if(this.grid?.api){
      this.totalRowsVisible = this.grid.api.getDisplayedRowCount();
      if (this.totalRowsVisible === 0) {
        this.grid.api.showNoRowsOverlay();
      } else {
        this.grid.api.hideOverlay();
      }
      this.controlHeightDynamically();
    }
  }

  ngOnDestroy(): void {
    this.gridOptions = {};
    this.tableData = [];
    this.externalData = [];
    this.columnDefinition = [];
    this.columnDefinitionSub = [];
    this.cleanupEvents?.();
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}

